import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  model,
  OnInit,
  signal
} from '@angular/core';
import {
  DayExpensesReleaseCalendar, EditPlanFactResult, PlanFactException,
  ProductExpensesParams,
  ReleaseCalendarSelectedOptions
} from "../../types/release-calendar-types";
import {UtilityService} from "../../../../../core/services/utility.service";
import { days } from '../../configs/release-calendar-config';
import {ReleaseCalendarService} from "../../services/release-calendar.service";
import {
  ReleaseCalendarEditPlanModalComponent
} from "../modals/release-calendar-edit-plan-modal/release-calendar-edit-plan-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationStatus} from "../../../../../core/types/confirmation-modal.types";
import {CurrentUserRoleAndLogin} from "../../../../../core/types/auth.types";
import {smoothShowHideWidth} from "../../../../../shared/animations/smoothShowHideAnimations";
import { MatButton, MatFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: 'app-release-calendar-product-expenses',
  standalone: true,
  imports: [
    MatButton,
    MatFabButton,
    MatTooltip,
    MatIcon
  ],
  templateUrl: './release-calendar-product-expenses.component.html',
  styleUrls: [
    './release-calendar-product-expenses.component.scss',
    '../release-calendar-components.styles.scss'
  ],
  animations: [ smoothShowHideWidth ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReleaseCalendarProductExpensesComponent implements OnInit {
  productExpenses = input.required<Array<DayExpensesReleaseCalendar>>();
  releaseCalendarSelectedOptions = input.required<ReleaseCalendarSelectedOptions>()
  productExpensesParams = input.required<ProductExpensesParams>()
  productId = input.required<number>()
  planFactExceptions = model.required<Array<PlanFactException>>()
  loginAndRoles = input.required<CurrentUserRoleAndLogin>()

  private utilityService: UtilityService = inject(UtilityService);
  private releaseCalendarService: ReleaseCalendarService = inject(ReleaseCalendarService);
  private dialog: MatDialog = inject(MatDialog);

  protected readonly days = days;
  public totalDays= signal<Array<number>>([]);
  public isVisible= signal<boolean>(true);
  public readonly dayExpensesMap = computed(() => {
    const result = new Map<number, {
      items: DayExpensesReleaseCalendar[];
      isLimitExceeded: boolean;
    }>();

    for (const expense of this.productExpenses()) {
      const day = expense.plannedDate['2'];
      const planFactExceptionLimit = this.getSelectedPlanFactExceptionLimit(day);
      const isLimitExceeded = expense.price
        ? this.releaseCalendarService.isLimitExceeded({
          price: expense.price,
          plannedPrice: expense.plannedPrice,
          planFactExceptionLimit
        })
        : false;

      const current = result.get(day) ?? { items: [], isLimitExceeded: false };
      current.items.push(expense);
      current.isLimitExceeded = current.isLimitExceeded || isLimitExceeded;
      result.set(day, current);
    }

    return result;
  });

  public ngOnInit(): void {
    this.totalDays.set(this.releaseCalendarService.generateDays(this.releaseCalendarSelectedOptions()));
  }

  public isSameDay(releaseCalendarAdvertisements: DayExpensesReleaseCalendar, dayLabel: number): boolean {
    return releaseCalendarAdvertisements.plannedDate['2'] === dayLabel;
  }

  public hasDataForDay(day: number): boolean {
    if (day === 0) { return false; }

    return this.productExpenses().some(ad => this.isSameDay(ad, day));
  }

  public toggleVisibility(): void {
    this.isVisible.update(i => !i);
  }

  public formatBudgetValue(value: number): string {
    return this.utilityService.formatBudgetValue({ value });
  }

  public handleEditPlanFact(day: number): void {
    const planFactExceptionsData = this.getPlanFactExceptionsData(day);

    this.dialog.open(ReleaseCalendarEditPlanModalComponent, {
      width: '400px',
      data: {
        planFactException: planFactExceptionsData,
        plannedDayPrice: this.productExpensesParams().plannedDayPrice
      }
    }).afterClosed().subscribe((result: EditPlanFactResult | ConfirmationStatus) => {
      if (result !== 'cancel') {
        this.updatePlanFactExceptions(result as EditPlanFactResult);
      }
    });
  }

  public getTooltip(day: number): string {
    const planFactExceptionLimit = this.getSelectedPlanFactExceptionLimit(day);
    return planFactExceptionLimit ? `Лимит: ${planFactExceptionLimit.toString()}` : 'Установить лимит';
  }

  public getVisibleEditButton(day: number): boolean {
    return !!this.getSelectedPlanFactExceptionLimit(day)
  }

  private getPlanFactExceptionsData(day: number): PlanFactException {
    const date = this.getSelectedDate(day);
    const planFactException = this.planFactExceptions().find(i => i.date === date);

    return {
      id: planFactException?.id || 0,
      date: date,
      dayLimit: planFactException?.dayLimit || 0,
      productId: this.productId()
    }
  }

  private updatePlanFactExceptions({ planFactException, action }: EditPlanFactResult): void {
    const current = this.planFactExceptions();

    switch (action) {
      case 'create':
        this.planFactExceptions.set([...current, planFactException]);
        break;

      case 'update':
        this.planFactExceptions.set(
          current.map(item =>
            item.date === planFactException.date
              ? { ...item, ...planFactException }
              : item
          )
        );
        break;

      case 'delete':
        this.planFactExceptions.set(
          current.filter(item => item.date !== planFactException.date)
        );
        break;
    }
  }

  private getSelectedPlanFactExceptionLimit(day: number): number | undefined {
    const date = this.getSelectedDate(day);
    return this.planFactExceptions().find(i => i.date === date)?.dayLimit;
  }

  private getSelectedDate(selectDay: number): string {
    const day = selectDay < 10 ? `0${selectDay}` : selectDay;
    const month = this.releaseCalendarSelectedOptions().month < 10 ? `0${this.releaseCalendarSelectedOptions().month}` : this.releaseCalendarSelectedOptions().month;
    return `${day}.${month}.${this.releaseCalendarSelectedOptions().year}`;
  }
}
